import hash from "@emotion/hash"
import styled from "@emotion/styled"
import { Icon } from "components/icon/Icon"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"
import { useUser } from "contexts/User"
import getMediaQuery from "css/breakpoints"
import { buttonReset } from "css/buttons"
import { fullGrid } from "css/grid"
import { body50, bold, headline30, legend100, legend110, legend150, normal } from "css/text"
import { motion, useAnimationControls } from "framer-motion"
import useSSR from "hooks/useSSR"
import { useId, useLayoutEffect, useRef, useState } from "react"

const cssCollapsed = `css-${hash("collapsed")}`
const cssExpanded = `css-${hash("expanded")}`
const cssOrphans = `css-${hash("orphans")}`

const Main = styled.div`
  ${fullGrid}
  /* display: flex; */
  flex-direction: column;
  position: relative;
  grid-column: main-start / main-end;
  width: 100%;

  > div {
    grid-column: doc;
  }

  > button {
    ${buttonReset}
    display: flex;
    width: max-content;
    margin-block-end: 0.5rem;
    position: relative;
    padding: 0.5rem;
    margin-inline-start: -0.5rem;
    transition: color 0.3s, fill 0.3s;
    align-items: center;

    ${getMediaQuery("m", { min: true })} {
      display: none;
    }

    @media (hover: hover) {
      &:hover {
        color: rgb(var(--green));
        fill: rgb(var(--green));
      }
    }

    > svg {
      transform: rotate(180deg);
      width: 1.125rem;
      height: 1.125rem;
    }
    > span {
      ${legend100}
      ${bold}
        font-size: 0.93rem;
      margin-inline-start: 0.6rem;
    }

    [dir="rtl"] & {
      svg {
        transform: scaleX(1);
      }
      > span {
        margin-inline-end: 0;
        margin-inline-start: 0.6rem;
      }
    }
  }
`

const Title = styled(motion.p)`
  /* ${headline30} */
  font-size: 1.18rem;
  ${bold}
  margin-block-end: 0.675rem;
  margin-block-start: 2.5rem;
  color: rgb(var(--light-black));

  em {
    font-style: normal;
    color: rgb(var(--green));
    display: contents;

    /* color: rgb(var(--light-green)); */
  }
`

const TitleInter = styled(Title)`
  /* ${headline30} */
  ${bold}
  font-size: 1.18rem;
  margin-block-end: 1.875rem;
  color: rgb(var(--light-black));
  &:last-of-type {
    margin-block-start: unset;
  }

  ${getMediaQuery("m")} {
    grid-column: col 1 / span 6;
    /* grid-column: col 5 / span 6; */
  }

  em {
    font-style: normal;
    color: rgb(var(--green));
    display: contents;

    /* color: rgb(var(--light-green)); */
  }
`

const Description = styled.p`
  ${legend100}
  ${normal}
  margin-block-end: 0.875rem;
  color: rgb(var(--light-black));
`

const DIV = styled.div``

const RegionsContainer = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  /* ${getMediaQuery("m", { max: true })} {
    grid-column: 1 / span 1 !important;
  } */
`

const NoList = styled.div`
  list-style: none;
  padding: 0;
  margin: 0;
  /* border-bottom: 0.5px solid #979797; */
  padding-block-end: 0.5rem;

  /* & div {
    margin-block-start: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  } */

  & li {
    /* ${legend110} */
    font-size: 1.18rem;
    /* ${bold} */
  }
`

const List = styled.li`
  list-style: none;
  padding: 0;
  margin: 0;
  border-bottom: 0.5px solid #979797;
  padding-block-end: 0.5rem;

  /* & div {
    margin-block-start: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  } */

  & li {
    font-size: 1.18rem;
  }
`

const Header = styled.span`
  /* ${headline30} */
  ${bold}
  font-size: 1.18rem;
  color: rgb(var(--green));

  button {
    all: unset;
    align-items: center;
    cursor: pointer;
    display: flex;
    height: 4.375rem;
    justify-content: space-between;
    transition: color 0.3s;
    width: 100%;

    ${getMediaQuery("s", { max: true })} {
      word-break: break-word;
    }

    @media (hover: hover) {
      :hover {
        color: rgb(var(--green));
      }
    }
  }
`

const LanguagePanel = styled(motion.div)`
  /* overflow: hidden; */
`

const LanguageList = styled(motion.ul)`
  columns: 2;

  &[data-alt="true"] {
    column-height: 3rem;
  }

  column-gap: var(--grid-gap);
  will-change: height, opacity, transform;

  ${getMediaQuery("l")} {
    &[data-footer="true"] {
      columns: 4;
    }
  }

  li {
    break-inside: avoid;
    white-space: nowrap;

    // Safari
    box-sizing: border-box;
    display: inline-block;
    width: 100%;
    margin-block-end: 0.675rem;
  }

  h5 {
    ${legend150}
    ${bold}
  }

  p {
    /* ${body50} */
    font-size: 1.18rem;
    hyphens: auto;
    text-wrap: pretty;

    ${getMediaQuery("m", { max: true })} {
      br {
        display: none;
      }
    }
  }

  /* ${getMediaQuery("m")} {
    padding-inline-end: calc(var(--grid-col-unit) + var(--grid-gap));
  } */
`

const Link = styled.a`
  /* ${legend110} */
  ${normal}
  font-size: 1rem;
  cursor: pointer;
  align-items: center;
  color: rgb(var(--light-black));
  display: inline-flex;
  gap: 0.5rem;
  text-decoration: none;
  transition: color 0.3s;

  [dir="rtl"] &:not([dir="rtl"]) {
    flex-direction: row-reverse;
  }

  @media (hover: hover) {
    :hover {
      color: rgb(var(--green));
    }
  }

  .active & {
    color: rgb(var(--green));
    cursor: default;

    // bullet
    ::after {
      background-color: currentColor;
      border-radius: 50%;
      content: "";
      height: 0.5rem;
      width: 0.5rem;
      margin-block-start: 2px;
    }
  }
`

const Dropdown = styled.span`
  --iconSize: 12px;
  --iconStrokeWidth: 2px;

  ::before,
  ::after {
    background: currentColor;
    content: "";
    display: block;
    height: var(--iconStrokeWidth);
    left: 0;
    position: absolute;
    right: 0;
    top: 50%;
    transition: transform 0.6s;
    will-change: transform;

    html.prefers-reduced-motion & {
      transition: none;
    }
  }

  .${cssCollapsed} & {
    ::after {
      transform: rotate(90deg);
    }
  }

  height: var(--iconSize);
  position: relative;
  width: var(--iconSize);

  .no-js & {
    display: none;
  }
`

export function regionBucket(regionRaw) {
  const s = (regionRaw ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()

  if (/(^|[^a-z])(africa|african)([^a-z]|$)/i.test(s)) return "african"
  if (/(^|[^a-z])(europe|european)([^a-z]|$)/i.test(s)) return "european"
  if (/(^|[^a-z])(america|americas|american)([^a-z]|$)/i.test(s)) return "american"
  if (/(^|[^a-z])(asia|asian|apac)([^a-z]|$)/i.test(s)) return "asian"

  return "other"
}

function RegionItem({ isInFooter, uidBase, region, label, locales, env, navigation, locale, setLocaleAndNavigate, prefersReducedMotion }) {
  const [isOpen, setIsOpen] = useState(false)
  const controls = useAnimationControls()
  const prevIsOpen = useRef(null)
  const { defaultLocale } = useLocale()
  const ssr = useSSR()
  // const console = useConsole()

  const expand = async () => {
    await controls.start({ height: "auto", visibility: "visible" })
    return controls.start({ opacity: 1, translateY: 0 })
  }
  const collapse = async () => {
    await controls.start({ opacity: 0, translateY: "-5%", margin: 0 })
    return controls.start({ height: 0, visibility: "hidden" })
  }

  useLayoutEffect(() => {
    if (prevIsOpen.current === null) {
      controls.set(
        isOpen
          ? { height: "auto", opacity: 1, translateY: 0, visibility: "visible" }
          : { height: 0, opacity: 0, translateY: "-5%", visibility: "hidden", margin: 0 }
      )
    } else {
      if (isOpen && prevIsOpen.current === false) {
        expand()
      } else if (!isOpen && prevIsOpen.current === true) {
        collapse()
      }
    }
    prevIsOpen.current = isOpen
  }, [isOpen, controls])

  const btnId = `${region}-btn`
  const panelId = `${region}-panel`

  return (
    <List>
      <div>
        <Header className={isOpen ? cssExpanded : cssCollapsed}>
          <button type='button' id={btnId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setIsOpen(v => !v)}>
            {label}
            <Dropdown aria-hidden='true' />
          </button>
        </Header>

        <LanguagePanel animate={controls} aria-hidden={!isOpen} id={panelId} transition={{ duration: prefersReducedMotion ? 0 : 0.3 }}>
          <LanguageList
            data-footer={(!!isInFooter).toString()}
            aria-labelledby={`${region}-btn`}
            id={`${region}-list`}
            className={locales.filter(item => item.text !== "").length === 2 ? cssOrphans : undefined}
          >
            {locales.map((_loc, idx) => {
              const loc = env.cn && _loc.codes.www === "zh-hans-cn" ? defaultLocale : _loc
              const isCurrent = loc?.codes.www === locale?.current.codes.www

              let href =
                env.cn && loc.external
                  ? new URL(navigation.localize(navigation.slug, loc?.codes.www), "https://rolex.com").href
                  : !env.cn && loc.codes.www === "zh-hans-cn"
                  ? new URL(navigation.slug, "https://www.rolex.cn").href
                  : navigation.localize(navigation.slug, loc?.codes.www)

              if (!ssr && window.location.search) {
                href += window.location.search
              }
              const isRawCurrent = loc?.codes?.www === locale?.current.codes?.www
              const isDuplicate = !!loc?.duplicate
              const duplicatedClass = isDuplicate ? "duplicate" : isRawCurrent ? "active" : null
              if (env.cn && loc.external) return null
              return (
                <li key={loc?.codes?.www || idx} className={duplicatedClass}>
                  <Link
                    href={href}
                    lang={loc?.codes?.www}
                    dir={loc.dir}
                    onClick={setLocaleAndNavigate}
                    target='_self'
                    rel='noopener'
                    aria-current={isCurrent || null}
                  >
                    {loc.label}
                  </Link>
                </li>
              )
            })}
          </LanguageList>
        </LanguagePanel>
      </div>
    </List>
  )
}

function InternationalRegions({ isInFooter, uidBase, region, /* locales,*/ env, navigation, locale, setLocaleAndNavigate, prefersReducedMotion }) {
  // const console = useConsole()
  const ssr = useSSR()
  // const controls = useAnimationControls()
  // const dictionary = useDictionary()
  // const labelGetters = {
  //   other: () => dictionary.languagesOtherMarket(),
  // }

  // const dictionary = useDictionary()

  // const btnId = `${region}-btn`
  const panelId = `${region[0].region}-panel`
  return (
    <NoList>
      <div>
        {/* <Header className={isOpen ? cssExpanded : cssCollapsed}> */}
        {/* <button type='button' id={btnId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setIsOpen(v => !v)}> */}
        {/* <div>{region[0].region}</div> */}
        {/* <Dropdown type='dropDown' aria-hidden='true' />
          </button> */}
        {/* </Header> */}

        <LanguagePanel /*animate={controls} aria-hidden={!isOpen}*/ id={panelId} /*transition={{ duration: prefersReducedMotion ? 0 : 0.3 }}*/>
          <LanguageList
            data-alt={env.cn}
            data-footer={(!!isInFooter).toString()}
            aria-labelledby={`language-${region[0].region}-heading`}
            id={`language-${region[0].region}-list`}
            // className={locales.filter(item => item.text !== "").length === 2 ? cssOrphans : undefined}
          >
            {(env.cn ? region[0].locales.toReversed() : region[0].locales).map((loc, idx) => {
              const isCurrent = loc?.codes.www === locale?.current.codes.www
              let href =
                env.cn && loc.external
                  ? new URL(navigation.localize(navigation.slug, loc?.codes.www), "https://rolex.com").href
                  : navigation.localize(navigation.slug, loc?.codes.www)

              if (!ssr && window.location.search) {
                href += window.location.search
              }
              const isRawCurrent = loc?.codes?.www === locale?.current.codes?.www
              const isDuplicate = !!loc?.duplicate
              const duplicatedClass = isDuplicate ? "duplicate" : isRawCurrent ? "active" : null
              if (env.cn && loc.external) return null
              return (
                <li key={loc?.codes?.www || idx} className={duplicatedClass}>
                  <Link
                    href={href}
                    lang={loc?.codes?.www}
                    dir={loc.dir}
                    onClick={setLocaleAndNavigate}
                    target='_self'
                    rel='noopener'
                    aria-current={isCurrent || null}
                  >
                    {loc.label}
                  </Link>
                </li>
              )
            })}
          </LanguageList>
        </LanguagePanel>
      </div>
    </NoList>
  )
}

export default function LanguageContainer({ isInFooter, switchBack, isInNav, setLocaleAndNavigate, hidden, title }) {
  const console = useConsole()
  const uid = useId()
  const env = useEnv()
  const locale = useLocale()
  const dictionary = useDictionary()
  const navigation = useNavigation()
  const ssr = useSSR()
  const prefersReducedMotion = !ssr && document.documentElement.classList.contains("prefers-reduced-motion")
  const user = useUser()
  const regions = locale.regions
  const removedOtherRegion = regions.filter(r => regionBucket(r.region) !== "other")
  const otherRegions = locale.regions.filter(r => regionBucket(r.region) === "other")

  const labelGetters = {
    african: () => dictionary.languagesAfricanMarket(),
    european: () => dictionary.languagesEuropeanMarket(),
    american: () => dictionary.languagesAmericanMarket(),
    asian: () => dictionary.languagesAsianMarket(),
    other: () => dictionary.languagesOtherMarket(),
  }

  const knownGenericCountryCodes = ["PS", "EH"]
  const getCountry = new Intl.DisplayNames([locale.current.codes.www], { type: "region" })
  const rcc = user?.realCountryCode
  let countryOfRCC = ""
  try {
    countryOfRCC = getCountry.of(rcc)
  } catch {}

  const dicLabelA = knownGenericCountryCodes.includes(rcc) ? "neutralAvailableLanguages" : "availableLanguages"
  const dicLabelB = knownGenericCountryCodes.includes(rcc) ? "neutralLanguageRegionDescription" : "languageRegionDescription"

  const style = Object.assign(
    {},
    hidden && {
      display: "none",
      contentVisibility: "hidden",
      position: "absolute",
    }
  )

  return (
    <Main style={style}>
      {/* {isInNav && (
        <button type='button' className='back' onClick={switchBack}>
          <Icon type='arrowBack' />
          <span>{dictionary.back()}</span>
        </button>
      )}*/}
      <div>
        <TitleInter
          id={isInFooter ? "language-other-heading-footer" : "language-other-heading"}
          dangerouslySetInnerHTML={{
            __html: countryOfRCC && dictionary[dicLabelA](countryOfRCC),
          }}
        />
      </div>
      <InternationalRegions
        isInFooter={isInFooter}
        key={otherRegions}
        uidBase={uid}
        region={otherRegions}
        env={env}
        navigation={navigation}
        locale={locale}
        setLocaleAndNavigate={setLocaleAndNavigate}
      />

      {!env.cn && (
        <>
          <div>
            <Title id='language-heading' dangerouslySetInnerHTML={{ __html: dictionary.chooseYourRegion() }} />
            <Description
              dangerouslySetInnerHTML={{
                __html: countryOfRCC && dictionary[dicLabelB](countryOfRCC),
              }}
            />
          </div>
          <DIV aria-labelledby='language-heading'>
            <RegionsContainer>
              {removedOtherRegion.map(({ region, locales }) => {
                if (!region) return null

                const bucket = regionBucket(region)
                const label = labelGetters[bucket]()
                return (
                  <RegionItem
                    isInFooter={isInFooter}
                    key={region}
                    uidBase={uid}
                    region={region}
                    label={label}
                    locales={locales}
                    env={env}
                    navigation={navigation}
                    locale={locale}
                    setLocaleAndNavigate={setLocaleAndNavigate}
                    prefersReducedMotion={prefersReducedMotion}
                  />
                )
              })}
            </RegionsContainer>
          </DIV>
        </>
      )}
    </Main>
  )
}
